/**
 * MKT — Afiliados (mock)
 *
 * 3 tabs: Programa | Afiliados | Conversiones
 * 4 KPIs arriba: Afiliados activos / Clicks 30d / Ventas atribuidas / Comisiones a pagar
 *
 * 100% mockeado con useState. No toca backend. Cuando se conecte:
 *   GET    /api/v1/affiliates/program
 *   PATCH  /api/v1/affiliates/program
 *   GET    /api/v1/affiliates           (?status=&q=)
 *   POST   /api/v1/affiliates
 *   PATCH  /api/v1/affiliates/:id
 *   GET    /api/v1/affiliates/conversions
 *   POST   /api/v1/affiliates/conversions/:id/approve|reject
 *   POST   /api/v1/affiliates/payouts
 */
import { createFileRoute } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import {
  Card, CardContent, Input, Label, Button, Badge, PageHeader,
} from '@/components/ui'
import {
  Plus, Users, MousePointerClick, ShoppingBag, Wallet, Search,
  Copy, Check, X, Edit, Trash2, ExternalLink, Settings2, Link2, DollarSign,
} from 'lucide-react'

export const Route = createFileRoute('/store/$id/affiliates')({
  component: StoreAffiliatesPage,
})

// ============================================================
// Types
// ============================================================

type AffiliateStatus = 'active' | 'paused' | 'pending' | 'banned'
type ConversionStatus = 'pending' | 'approved' | 'paid' | 'rejected'

interface AffiliateProgram {
  active: boolean
  defaultCommissionPercent: number
  cookieDays: number
  minPayoutAmount: number
  termsUrl: string | null
  publicSignupOpen: boolean
}

interface Affiliate {
  id: string
  email: string
  name: string
  slug: string
  status: AffiliateStatus
  commissionPercentOverride: number | null
  totalClicks: number
  totalConversions: number
  totalSales: number
  totalCommissions: number
  pendingCommissions: number
  paidCommissions: number
  joinedAt: string
  paymentMethod: { type: 'bank' | 'nequi' | 'paypal'; details: string } | null
}

interface Conversion {
  id: string
  affiliateId: string
  affiliateName: string
  orderNumber: string
  customerEmail: string
  saleAmount: number
  commissionAmount: number
  status: ConversionStatus
  occurredAt: string
}

// ============================================================
// Mock seed
// ============================================================

const SEED_PROGRAM: AffiliateProgram = {
  active: true,
  defaultCommissionPercent: 10,
  cookieDays: 30,
  minPayoutAmount: 100000,
  termsUrl: 'https://skeleton.com.co/legal/afiliados',
  publicSignupOpen: false,
}

const SEED_AFFILIATES: Affiliate[] = [
  {
    id: 'aff_01', email: 'sofia@example.com', name: 'Sofía Rodríguez', slug: 'sofiar',
    status: 'active', commissionPercentOverride: 15,
    totalClicks: 412, totalConversions: 23, totalSales: 6_140_000,
    totalCommissions: 921_000, pendingCommissions: 318_000, paidCommissions: 603_000,
    joinedAt: '2026-02-14',
    paymentMethod: { type: 'nequi', details: '300 234 5678' },
  },
  {
    id: 'aff_02', email: 'andres@example.com', name: 'Andrés Loaiza', slug: 'andres-l',
    status: 'active', commissionPercentOverride: null,
    totalClicks: 198, totalConversions: 11, totalSales: 2_750_000,
    totalCommissions: 275_000, pendingCommissions: 110_000, paidCommissions: 165_000,
    joinedAt: '2026-03-02',
    paymentMethod: { type: 'bank', details: 'Bancolombia · Ahorros · ****8821' },
  },
  {
    id: 'aff_03', email: 'mariana@example.com', name: 'Mariana Ortiz', slug: 'mariana_o',
    status: 'paused', commissionPercentOverride: 12,
    totalClicks: 88, totalConversions: 4, totalSales: 980_000,
    totalCommissions: 117_600, pendingCommissions: 0, paidCommissions: 117_600,
    joinedAt: '2026-01-08',
    paymentMethod: { type: 'paypal', details: 'mariana.ortiz@example.com' },
  },
  {
    id: 'aff_04', email: 'creator@dropxs.co', name: 'DropXS', slug: 'dropxs',
    status: 'active', commissionPercentOverride: null,
    totalClicks: 1_402, totalConversions: 67, totalSales: 17_300_000,
    totalCommissions: 1_730_000, pendingCommissions: 612_000, paidCommissions: 1_118_000,
    joinedAt: '2025-11-22',
    paymentMethod: { type: 'bank', details: 'Davivienda · Corriente · ****0012' },
  },
  {
    id: 'aff_05', email: 'ricardo@example.com', name: 'Ricardo Pérez', slug: 'ricardop',
    status: 'pending', commissionPercentOverride: null,
    totalClicks: 0, totalConversions: 0, totalSales: 0,
    totalCommissions: 0, pendingCommissions: 0, paidCommissions: 0,
    joinedAt: '2026-05-06',
    paymentMethod: null,
  },
  {
    id: 'aff_06', email: 'lina@example.com', name: 'Lina Castro', slug: 'lina',
    status: 'active', commissionPercentOverride: null,
    totalClicks: 56, totalConversions: 2, totalSales: 410_000,
    totalCommissions: 41_000, pendingCommissions: 41_000, paidCommissions: 0,
    joinedAt: '2026-04-20',
    paymentMethod: { type: 'nequi', details: '301 567 8910' },
  },
  {
    id: 'aff_07', email: 'spam@throwaway.io', name: 'Spam Bot', slug: 'spam',
    status: 'banned', commissionPercentOverride: null,
    totalClicks: 5_032, totalConversions: 0, totalSales: 0,
    totalCommissions: 0, pendingCommissions: 0, paidCommissions: 0,
    joinedAt: '2026-04-30',
    paymentMethod: null,
  },
]

const SEED_CONVERSIONS: Conversion[] = [
  { id: 'cv_001', affiliateId: 'aff_01', affiliateName: 'Sofía Rodríguez', orderNumber: 'ORD-MOXBTC7H-V4XJ', customerEmail: 'cliente1@gmail.com', saleAmount: 240_000, commissionAmount: 36_000, status: 'pending',  occurredAt: '2026-05-08' },
  { id: 'cv_002', affiliateId: 'aff_04', affiliateName: 'DropXS',           orderNumber: 'ORD-MOXC84JB-YCON', customerEmail: 'cliente2@gmail.com', saleAmount: 295_000, commissionAmount: 29_500, status: 'pending',  occurredAt: '2026-05-08' },
  { id: 'cv_003', affiliateId: 'aff_04', affiliateName: 'DropXS',           orderNumber: 'ORD-MOXCA06Y-LYLU', customerEmail: 'cliente3@gmail.com', saleAmount: 430_000, commissionAmount: 43_000, status: 'approved', occurredAt: '2026-05-07' },
  { id: 'cv_004', affiliateId: 'aff_02', affiliateName: 'Andrés Loaiza',    orderNumber: 'ORD-MOX9XL2P-FFFF', customerEmail: 'cliente4@gmail.com', saleAmount: 195_000, commissionAmount: 19_500, status: 'paid',     occurredAt: '2026-05-05' },
  { id: 'cv_005', affiliateId: 'aff_06', affiliateName: 'Lina Castro',      orderNumber: 'ORD-MOX8XL2P-GGGG', customerEmail: 'cliente5@gmail.com', saleAmount: 410_000, commissionAmount: 41_000, status: 'pending',  occurredAt: '2026-05-04' },
  { id: 'cv_006', affiliateId: 'aff_01', affiliateName: 'Sofía Rodríguez',  orderNumber: 'ORD-MOX7XL2P-HHHH', customerEmail: 'cliente6@gmail.com', saleAmount: 275_000, commissionAmount: 41_250, status: 'paid',     occurredAt: '2026-05-03' },
  { id: 'cv_007', affiliateId: 'aff_04', affiliateName: 'DropXS',           orderNumber: 'ORD-MOX6XL2P-IIII', customerEmail: 'cliente7@gmail.com', saleAmount: 540_000, commissionAmount: 54_000, status: 'rejected', occurredAt: '2026-05-02' },
  { id: 'cv_008', affiliateId: 'aff_01', affiliateName: 'Sofía Rodríguez',  orderNumber: 'ORD-MOX5XL2P-JJJJ', customerEmail: 'cliente8@gmail.com', saleAmount: 195_000, commissionAmount: 29_250, status: 'approved', occurredAt: '2026-05-01' },
]

// ============================================================
// Helpers
// ============================================================

function fmtMoney(amount: number): string {
  return `$ ${amount.toLocaleString('es-CO')}`
}

function fmtDate(d: string): string {
  const date = new Date(d)
  return date.toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' })
}

const STATUS_META: Record<AffiliateStatus, { label: string; variant: Parameters<typeof Badge>[0]['variant'] }> = {
  active:  { label: 'Activo',     variant: 'success' },
  paused:  { label: 'Pausado',    variant: 'warning' },
  pending: { label: 'Pendiente',  variant: 'default' },
  banned:  { label: 'Baneado',    variant: 'destructive' },
}

const CONVERSION_STATUS_META: Record<ConversionStatus, { label: string; variant: Parameters<typeof Badge>[0]['variant'] }> = {
  pending:  { label: 'Pendiente', variant: 'warning' },
  approved: { label: 'Aprobada',  variant: 'success' },
  paid:     { label: 'Pagada',    variant: 'default' },
  rejected: { label: 'Rechazada', variant: 'destructive' },
}

// ============================================================
// Page
// ============================================================

function StoreAffiliatesPage() {
  const { id } = Route.useParams()
  const storeUrl = `https://${id}.skeleton-store.com`

  const [tab, setTab] = useState<'program' | 'list' | 'conversions'>('program')
  const [program, setProgram] = useState<AffiliateProgram>(SEED_PROGRAM)
  const [affiliates, setAffiliates] = useState<Affiliate[]>(SEED_AFFILIATES)
  const [conversions, setConversions] = useState<Conversion[]>(SEED_CONVERSIONS)

  const kpis = useMemo(() => {
    const activeCount = affiliates.filter(a => a.status === 'active').length
    const clicks30d = affiliates.reduce((s, a) => s + a.totalClicks, 0)
    const sales30d = conversions.reduce((s, c) => s + c.saleAmount, 0)
    const pendingCommissions = affiliates.reduce((s, a) => s + a.pendingCommissions, 0)
    return { activeCount, clicks30d, sales30d, pendingCommissions }
  }, [affiliates, conversions])

  return (
    <>
      <PageHeader
        title="Afiliados"
        description="Programa de referidos: links únicos, comisiones por venta y payouts"
        action={
          <Badge variant={program.active ? 'success' : 'destructive'}>
            {program.active ? 'Programa activo' : 'Programa inactivo'}
          </Badge>
        }
      />

      <div className="p-8 space-y-6">
        {/* KPIs */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard icon={<Users className="size-4" />}              label="Afiliados activos"   value={String(kpis.activeCount)} />
          <KpiCard icon={<MousePointerClick className="size-4" />}  label="Clicks (30d)"        value={kpis.clicks30d.toLocaleString('es-CO')} />
          <KpiCard icon={<ShoppingBag className="size-4" />}        label="Ventas atribuidas"   value={fmtMoney(kpis.sales30d)} />
          <KpiCard icon={<Wallet className="size-4" />}             label="Comisiones a pagar"  value={fmtMoney(kpis.pendingCommissions)} accent />
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 border-b border-border">
          <TabButton active={tab === 'program'}     onClick={() => setTab('program')}><Settings2 className="size-3.5" /> Programa</TabButton>
          <TabButton active={tab === 'list'}        onClick={() => setTab('list')}><Users className="size-3.5" /> Afiliados ({affiliates.length})</TabButton>
          <TabButton active={tab === 'conversions'} onClick={() => setTab('conversions')}><DollarSign className="size-3.5" /> Conversiones ({conversions.length})</TabButton>
        </div>

        {tab === 'program' && (
          <ProgramTab program={program} setProgram={setProgram} storeUrl={storeUrl} />
        )}
        {tab === 'list' && (
          <AffiliatesTab affiliates={affiliates} setAffiliates={setAffiliates} program={program} storeUrl={storeUrl} />
        )}
        {tab === 'conversions' && (
          <ConversionsTab conversions={conversions} setConversions={setConversions} setAffiliates={setAffiliates} />
        )}
      </div>
    </>
  )
}

// ============================================================
// Reusable bits
// ============================================================

function KpiCard({ icon, label, value, accent }: { icon: React.ReactNode; label: string; value: string; accent?: boolean }) {
  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-center gap-2 text-muted-foreground text-xs mb-2">
          {icon}
          <span>{label}</span>
        </div>
        <p className={`text-2xl font-semibold ${accent ? 'text-[hsl(var(--primary))]' : 'text-foreground'}`}>{value}</p>
      </CardContent>
    </Card>
  )
}

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
        active
          ? 'border-[hsl(var(--primary))] text-foreground'
          : 'border-transparent text-muted-foreground hover:text-foreground'
      }`}
    >
      {children}
    </button>
  )
}

// ============================================================
// Tab: Programa
// ============================================================

function ProgramTab({ program, setProgram, storeUrl }: {
  program: AffiliateProgram
  setProgram: React.Dispatch<React.SetStateAction<AffiliateProgram>>
  storeUrl: string
}) {
  const [draft, setDraft] = useState(program)
  const [saved, setSaved] = useState(false)
  const dirty = JSON.stringify(draft) !== JSON.stringify(program)

  const save = () => {
    setProgram(draft)
    setSaved(true)
    setTimeout(() => setSaved(false), 1800)
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-6">
        <Card>
          <CardContent className="p-6 space-y-5">
            <h3 className="text-sm font-semibold text-foreground">Configuración general</h3>

            <div className="flex items-start justify-between gap-4 p-3 rounded border border-border bg-[hsl(var(--surface))]">
              <div>
                <p className="text-sm text-foreground font-medium">Programa activo</p>
                <p className="text-xs text-muted-foreground mt-0.5">Si está apagado, los links de afiliados no atribuyen ventas.</p>
              </div>
              <ToggleSwitch
                checked={draft.active}
                onChange={(v) => setDraft({ ...draft, active: v })}
              />
            </div>

            <div className="flex items-start justify-between gap-4 p-3 rounded border border-border bg-[hsl(var(--surface))]">
              <div>
                <p className="text-sm text-foreground font-medium">Auto-registro público</p>
                <p className="text-xs text-muted-foreground mt-0.5">Permitir que cualquier visitante se registre como afiliado (queda en estado pending).</p>
              </div>
              <ToggleSwitch
                checked={draft.publicSignupOpen}
                onChange={(v) => setDraft({ ...draft, publicSignupOpen: v })}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="commission">Comisión por defecto (%)</Label>
                <Input
                  id="commission" type="number" min={0} max={100}
                  value={draft.defaultCommissionPercent}
                  onChange={(e) => setDraft({ ...draft, defaultCommissionPercent: Number(e.target.value) || 0 })}
                />
                <p className="text-[11px] text-muted-foreground mt-1">Override por afiliado disponible.</p>
              </div>
              <div>
                <Label htmlFor="cookie">Cookie tracking (días)</Label>
                <Input
                  id="cookie" type="number" min={1} max={365}
                  value={draft.cookieDays}
                  onChange={(e) => setDraft({ ...draft, cookieDays: Number(e.target.value) || 0 })}
                />
                <p className="text-[11px] text-muted-foreground mt-1">Última fuente gana.</p>
              </div>
              <div>
                <Label htmlFor="minpayout">Mínimo para payout</Label>
                <Input
                  id="minpayout" type="number" min={0}
                  value={draft.minPayoutAmount}
                  onChange={(e) => setDraft({ ...draft, minPayoutAmount: Number(e.target.value) || 0 })}
                />
                <p className="text-[11px] text-muted-foreground mt-1">{fmtMoney(draft.minPayoutAmount)} COP</p>
              </div>
            </div>

            <div>
              <Label htmlFor="terms">URL de términos y condiciones</Label>
              <Input
                id="terms" type="url" placeholder="https://tu-tienda.com/legal/afiliados"
                value={draft.termsUrl ?? ''}
                onChange={(e) => setDraft({ ...draft, termsUrl: e.target.value || null })}
              />
            </div>

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
              {saved && (
                <span className="text-xs text-green-700 dark:text-green-400 inline-flex items-center gap-1">
                  <Check className="size-3.5" /> Guardado
                </span>
              )}
              <Button variant="ghost" disabled={!dirty} onClick={() => setDraft(program)}>Descartar</Button>
              <Button disabled={!dirty} onClick={save}>Guardar cambios</Button>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="space-y-4">
        <Card>
          <CardContent className="p-5">
            <h3 className="text-sm font-semibold text-foreground mb-3">Cómo funciona</h3>
            <ol className="space-y-3 text-sm text-foreground">
              <li className="flex gap-3">
                <span className="size-5 rounded bg-[hsl(var(--primary)/0.15)] text-[hsl(var(--primary))] text-[11px] flex items-center justify-center shrink-0 mt-0.5">1</span>
                <span>El afiliado comparte su link único: <code className="text-[11px] text-muted-foreground">{storeUrl}/?ref=su-slug</code></span>
              </li>
              <li className="flex gap-3">
                <span className="size-5 rounded bg-[hsl(var(--primary)/0.15)] text-[hsl(var(--primary))] text-[11px] flex items-center justify-center shrink-0 mt-0.5">2</span>
                <span>El visitante recibe una cookie de <strong>{draft.cookieDays} días</strong>.</span>
              </li>
              <li className="flex gap-3">
                <span className="size-5 rounded bg-[hsl(var(--primary)/0.15)] text-[hsl(var(--primary))] text-[11px] flex items-center justify-center shrink-0 mt-0.5">3</span>
                <span>Si compra dentro de la ventana, se atribuye la venta y se calcula la comisión (<strong>{draft.defaultCommissionPercent}%</strong> por defecto).</span>
              </li>
              <li className="flex gap-3">
                <span className="size-5 rounded bg-[hsl(var(--primary)/0.15)] text-[hsl(var(--primary))] text-[11px] flex items-center justify-center shrink-0 mt-0.5">4</span>
                <span>Aprobás conversiones y pagás cuando supere el mínimo de <strong>{fmtMoney(draft.minPayoutAmount)}</strong>.</span>
              </li>
            </ol>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

function ToggleSwitch({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${checked ? 'bg-[hsl(var(--primary))]' : 'bg-[hsl(var(--muted))]'}`}
    >
      <span className={`inline-block size-4 transform rounded-full bg-white transition-transform ${checked ? 'translate-x-6' : 'translate-x-1'}`} />
    </button>
  )
}

// ============================================================
// Tab: Lista de afiliados
// ============================================================

function AffiliatesTab({ affiliates, setAffiliates, program, storeUrl }: {
  affiliates: Affiliate[]
  setAffiliates: React.Dispatch<React.SetStateAction<Affiliate[]>>
  program: AffiliateProgram
  storeUrl: string
}) {
  const [q, setQ] = useState('')
  const [statusFilter, setStatusFilter] = useState<AffiliateStatus | 'all'>('all')
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<Affiliate | null>(null)
  const [copiedId, setCopiedId] = useState<string | null>(null)

  const filtered = useMemo(() => {
    return affiliates.filter(a => {
      if (statusFilter !== 'all' && a.status !== statusFilter) return false
      if (q.trim()) {
        const needle = q.toLowerCase()
        if (!a.name.toLowerCase().includes(needle) &&
            !a.email.toLowerCase().includes(needle) &&
            !a.slug.toLowerCase().includes(needle)) return false
      }
      return true
    })
  }, [affiliates, q, statusFilter])

  const copyLink = (slug: string, id: string) => {
    const link = `${storeUrl}/?ref=${slug}`
    navigator.clipboard.writeText(link)
    setCopiedId(id)
    setTimeout(() => setCopiedId(null), 1500)
  }

  const updateAffiliate = (updated: Affiliate) => {
    setAffiliates(prev => prev.map(a => a.id === updated.id ? updated : a))
  }

  const deleteAffiliate = (id: string) => {
    if (!confirm('¿Eliminar este afiliado? Las conversiones históricas quedarán huérfanas.')) return
    setAffiliates(prev => prev.filter(a => a.id !== id))
  }

  const createAffiliate = (a: Omit<Affiliate, 'id' | 'totalClicks' | 'totalConversions' | 'totalSales' | 'totalCommissions' | 'pendingCommissions' | 'paidCommissions' | 'joinedAt'>) => {
    const newOne: Affiliate = {
      ...a,
      id: `aff_${Math.random().toString(36).slice(2, 8)}`,
      totalClicks: 0, totalConversions: 0, totalSales: 0,
      totalCommissions: 0, pendingCommissions: 0, paidCommissions: 0,
      joinedAt: new Date().toISOString().slice(0, 10),
    }
    setAffiliates(prev => [newOne, ...prev])
  }

  return (
    <Card>
      <CardContent className="p-0">
        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row gap-3 p-4 border-b border-border">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por nombre, email o slug…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as AffiliateStatus | 'all')}
            className="bg-[hsl(var(--surface))] border border-border rounded px-3 py-2 text-sm text-foreground"
          >
            <option value="all">Todos los estados</option>
            <option value="active">Activos</option>
            <option value="paused">Pausados</option>
            <option value="pending">Pendientes</option>
            <option value="banned">Baneados</option>
          </select>
          <Button onClick={() => setCreating(true)}><Plus className="size-4 mr-1" /> Nuevo afiliado</Button>
        </div>

        {/* Table */}
        {filtered.length === 0 ? (
          <div className="p-12 text-center text-sm text-muted-foreground">Sin resultados con los filtros actuales.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-muted-foreground border-b border-border">
                  <th className="px-4 py-3">Afiliado</th>
                  <th className="px-4 py-3">Link</th>
                  <th className="px-4 py-3 text-center">Estado</th>
                  <th className="px-4 py-3 text-right">Clicks</th>
                  <th className="px-4 py-3 text-right">Conv.</th>
                  <th className="px-4 py-3 text-right">Ventas</th>
                  <th className="px-4 py-3 text-right">Pendiente</th>
                  <th className="px-4 py-3 text-right">Comisión</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(a => {
                  const commission = a.commissionPercentOverride ?? program.defaultCommissionPercent
                  const isOverride = a.commissionPercentOverride !== null
                  return (
                    <tr key={a.id} className="border-b border-border hover:bg-[hsl(var(--surface))]">
                      <td className="px-4 py-3">
                        <p className="text-foreground font-medium">{a.name}</p>
                        <p className="text-[11px] text-muted-foreground">{a.email}</p>
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => copyLink(a.slug, a.id)}
                          className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground group"
                          title="Copiar link"
                        >
                          <Link2 className="size-3.5" />
                          <code className="font-mono">?ref={a.slug}</code>
                          {copiedId === a.id ? <Check className="size-3 text-green-700 dark:text-green-400" /> : <Copy className="size-3 opacity-0 group-hover:opacity-100" />}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Badge variant={STATUS_META[a.status].variant}>{STATUS_META[a.status].label}</Badge>
                      </td>
                      <td className="px-4 py-3 text-right text-foreground">{a.totalClicks.toLocaleString('es-CO')}</td>
                      <td className="px-4 py-3 text-right text-foreground">{a.totalConversions}</td>
                      <td className="px-4 py-3 text-right text-foreground">{fmtMoney(a.totalSales)}</td>
                      <td className="px-4 py-3 text-right text-[hsl(var(--primary))]">{a.pendingCommissions > 0 ? fmtMoney(a.pendingCommissions) : '—'}</td>
                      <td className="px-4 py-3 text-right">
                        <span className={`text-xs ${isOverride ? 'text-[hsl(var(--primary))]' : 'text-muted-foreground'}`}>{commission}%</span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => setEditing(a)} className="p-1.5 text-muted-foreground hover:text-foreground rounded" title="Editar">
                            <Edit className="size-3.5" />
                          </button>
                          <button onClick={() => deleteAffiliate(a.id)} className="p-1.5 text-muted-foreground hover:text-red-400 rounded" title="Eliminar">
                            <Trash2 className="size-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>

      {creating && (
        <AffiliateFormModal
          mode="create"
          program={program}
          onClose={() => setCreating(false)}
          onSubmit={(data) => { createAffiliate(data); setCreating(false) }}
        />
      )}
      {editing && (
        <AffiliateFormModal
          mode="edit"
          program={program}
          initial={editing}
          onClose={() => setEditing(null)}
          onSubmit={(data) => {
            updateAffiliate({ ...editing, ...data })
            setEditing(null)
          }}
        />
      )}
    </Card>
  )
}

function AffiliateFormModal({ mode, initial, program, onClose, onSubmit }: {
  mode: 'create' | 'edit'
  initial?: Affiliate
  program: AffiliateProgram
  onClose: () => void
  onSubmit: (data: {
    email: string
    name: string
    slug: string
    status: AffiliateStatus
    commissionPercentOverride: number | null
    paymentMethod: Affiliate['paymentMethod']
  }) => void
}) {
  const [name, setName] = useState(initial?.name ?? '')
  const [email, setEmail] = useState(initial?.email ?? '')
  const [slug, setSlug] = useState(initial?.slug ?? '')
  const [status, setStatus] = useState<AffiliateStatus>(initial?.status ?? 'pending')
  const [overrideEnabled, setOverrideEnabled] = useState(initial?.commissionPercentOverride !== null && initial?.commissionPercentOverride !== undefined)
  const [overrideValue, setOverrideValue] = useState(initial?.commissionPercentOverride ?? program.defaultCommissionPercent)
  const [payType, setPayType] = useState<'bank' | 'nequi' | 'paypal' | ''>(initial?.paymentMethod?.type ?? '')
  const [payDetails, setPayDetails] = useState(initial?.paymentMethod?.details ?? '')

  const submit = () => {
    if (!name.trim() || !email.trim() || !slug.trim()) return
    onSubmit({
      name: name.trim(),
      email: email.trim(),
      slug: slug.trim().toLowerCase().replace(/\s+/g, '-'),
      status,
      commissionPercentOverride: overrideEnabled ? overrideValue : null,
      paymentMethod: payType ? { type: payType, details: payDetails.trim() } : null,
    })
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="w-full max-w-lg bg-background border border-border rounded-lg shadow-xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h3 className="text-sm font-semibold text-foreground">
            {mode === 'create' ? 'Nuevo afiliado' : 'Editar afiliado'}
          </h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="size-4" />
          </button>
        </div>

        <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Nombre *</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Sofía Rodríguez" />
            </div>
            <div>
              <Label>Email *</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="sofia@example.com" />
            </div>
          </div>

          <div>
            <Label>Slug del link *</Label>
            <Input
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="sofiar"
            />
            <p className="text-[11px] text-muted-foreground mt-1">URL final: <code>?ref={slug || 'tu-slug'}</code></p>
          </div>

          <div>
            <Label>Estado</Label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as AffiliateStatus)}
              className="w-full bg-[hsl(var(--surface))] border border-border rounded px-3 py-2 text-sm text-foreground"
            >
              <option value="active">Activo</option>
              <option value="paused">Pausado</option>
              <option value="pending">Pendiente</option>
              <option value="banned">Baneado</option>
            </select>
          </div>

          <div className="border-t border-border pt-4">
            <div className="flex items-center justify-between mb-2">
              <Label className="!mb-0">Comisión personalizada</Label>
              <ToggleSwitch checked={overrideEnabled} onChange={setOverrideEnabled} />
            </div>
            {overrideEnabled ? (
              <Input
                type="number" min={0} max={100}
                value={overrideValue}
                onChange={(e) => setOverrideValue(Number(e.target.value) || 0)}
              />
            ) : (
              <p className="text-xs text-muted-foreground">Usa el % por defecto del programa ({program.defaultCommissionPercent}%).</p>
            )}
          </div>

          <div className="border-t border-border pt-4">
            <Label>Método de pago</Label>
            <div className="grid grid-cols-2 gap-2">
              <select
                value={payType}
                onChange={(e) => setPayType(e.target.value as 'bank' | 'nequi' | 'paypal' | '')}
                className="bg-[hsl(var(--surface))] border border-border rounded px-3 py-2 text-sm text-foreground"
              >
                <option value="">— Ninguno —</option>
                <option value="bank">Cuenta bancaria</option>
                <option value="nequi">Nequi</option>
                <option value="paypal">PayPal</option>
              </select>
              <Input
                value={payDetails}
                onChange={(e) => setPayDetails(e.target.value)}
                placeholder={payType === 'bank' ? 'Banco · Tipo · ****' : payType === 'nequi' ? '300 123 4567' : 'email@paypal.com'}
                disabled={!payType}
              />
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-border">
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={!name.trim() || !email.trim() || !slug.trim()}>
            {mode === 'create' ? 'Crear afiliado' : 'Guardar cambios'}
          </Button>
        </div>
      </div>
    </div>
  )
}

// ============================================================
// Tab: Conversiones
// ============================================================

function ConversionsTab({ conversions, setConversions, setAffiliates }: {
  conversions: Conversion[]
  setConversions: React.Dispatch<React.SetStateAction<Conversion[]>>
  setAffiliates: React.Dispatch<React.SetStateAction<Affiliate[]>>
}) {
  const [statusFilter, setStatusFilter] = useState<ConversionStatus | 'all'>('all')

  const filtered = useMemo(() => {
    if (statusFilter === 'all') return conversions
    return conversions.filter(c => c.status === statusFilter)
  }, [conversions, statusFilter])

  const summary = useMemo(() => {
    const totals: Record<ConversionStatus, number> = { pending: 0, approved: 0, paid: 0, rejected: 0 }
    conversions.forEach(c => { totals[c.status] += c.commissionAmount })
    return totals
  }, [conversions])

  // Acciones — actualizan también las comisiones del afiliado para mantener consistencia visual.
  const setStatus = (id: string, newStatus: ConversionStatus) => {
    const conv = conversions.find(c => c.id === id)
    if (!conv) return
    setConversions(prev => prev.map(c => c.id === id ? { ...c, status: newStatus } : c))
    setAffiliates(prev => prev.map(a => {
      if (a.id !== conv.affiliateId) return a
      const wasPending = conv.status === 'pending' || conv.status === 'approved'
      const wasPaid = conv.status === 'paid'
      const willBePending = newStatus === 'pending' || newStatus === 'approved'
      const willBePaid = newStatus === 'paid'
      let pending = a.pendingCommissions
      let paid = a.paidCommissions
      if (wasPending && !willBePending) pending -= conv.commissionAmount
      if (!wasPending && willBePending) pending += conv.commissionAmount
      if (wasPaid && !willBePaid) paid -= conv.commissionAmount
      if (!wasPaid && willBePaid) paid += conv.commissionAmount
      return { ...a, pendingCommissions: Math.max(0, pending), paidCommissions: Math.max(0, paid) }
    }))
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <SummaryCard label="Pendiente"  value={fmtMoney(summary.pending)}  variant="warning" />
        <SummaryCard label="Aprobada"   value={fmtMoney(summary.approved)} variant="success" />
        <SummaryCard label="Pagada"     value={fmtMoney(summary.paid)}     variant="default" />
        <SummaryCard label="Rechazada"  value={fmtMoney(summary.rejected)} variant="danger" />
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="flex items-center justify-between p-4 border-b border-border">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as ConversionStatus | 'all')}
              className="bg-[hsl(var(--surface))] border border-border rounded px-3 py-2 text-sm text-foreground"
            >
              <option value="all">Todas las conversiones</option>
              <option value="pending">Pendientes</option>
              <option value="approved">Aprobadas</option>
              <option value="paid">Pagadas</option>
              <option value="rejected">Rechazadas</option>
            </select>
            <p className="text-xs text-muted-foreground">{filtered.length} conversión{filtered.length === 1 ? '' : 'es'}</p>
          </div>

          {filtered.length === 0 ? (
            <div className="p-12 text-center text-sm text-muted-foreground">Sin conversiones con ese filtro.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wider text-muted-foreground border-b border-border">
                    <th className="px-4 py-3">Fecha</th>
                    <th className="px-4 py-3">Afiliado</th>
                    <th className="px-4 py-3">Orden</th>
                    <th className="px-4 py-3">Cliente</th>
                    <th className="px-4 py-3 text-right">Venta</th>
                    <th className="px-4 py-3 text-right">Comisión</th>
                    <th className="px-4 py-3 text-center">Estado</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(c => (
                    <tr key={c.id} className="border-b border-border hover:bg-[hsl(var(--surface))]">
                      <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{fmtDate(c.occurredAt)}</td>
                      <td className="px-4 py-3 text-foreground">{c.affiliateName}</td>
                      <td className="px-4 py-3">
                        <code className="text-xs text-foreground">{c.orderNumber}</code>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground text-xs">{c.customerEmail}</td>
                      <td className="px-4 py-3 text-right text-foreground">{fmtMoney(c.saleAmount)}</td>
                      <td className="px-4 py-3 text-right text-[hsl(var(--primary))] font-medium">{fmtMoney(c.commissionAmount)}</td>
                      <td className="px-4 py-3 text-center">
                        <Badge variant={CONVERSION_STATUS_META[c.status].variant}>
                          {CONVERSION_STATUS_META[c.status].label}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <ConversionActions current={c.status} onChange={(s) => setStatus(c.id, s)} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

function SummaryCard({ label, value, variant }: { label: string; value: string; variant: 'default' | 'success' | 'warning' | 'danger' }) {
  const colors: Record<typeof variant, string> = {
    default: 'text-foreground',
    success: 'text-green-700 dark:text-green-400',
    warning: 'text-[hsl(var(--primary))]',
    danger:  'text-red-700 dark:text-red-400',
  }
  return (
    <Card>
      <CardContent className="p-4">
        <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">{label}</p>
        <p className={`text-lg font-semibold ${colors[variant]}`}>{value}</p>
      </CardContent>
    </Card>
  )
}

function ConversionActions({ current, onChange }: { current: ConversionStatus; onChange: (s: ConversionStatus) => void }) {
  if (current === 'pending') {
    return (
      <div className="flex items-center justify-end gap-1">
        <button onClick={() => onChange('approved')} className="text-[11px] px-2 py-1 rounded bg-green-100 dark:bg-green-500/10 text-green-700 dark:text-green-300 hover:bg-green-500/20">Aprobar</button>
        <button onClick={() => onChange('rejected')} className="text-[11px] px-2 py-1 rounded bg-red-100 dark:bg-red-500/10 text-red-700 dark:text-red-300 hover:bg-red-500/20">Rechazar</button>
      </div>
    )
  }
  if (current === 'approved') {
    return (
      <button onClick={() => onChange('paid')} className="text-[11px] px-2 py-1 rounded bg-[hsl(var(--primary)/0.1)] text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary)/0.2)] inline-flex items-center gap-1">
        <ExternalLink className="size-3" /> Marcar pagada
      </button>
    )
  }
  return <span className="text-[11px] text-muted-foreground">—</span>
}
